import type { RouterProps } from '@lmc/common/types'
import type { AboutMe, Carer, Resident } from '@lmc/ts-common'
import {
  Button,
  CHAR_LIMIT,
  MultiTextArea,
  PageHeader,
  textLength
} from '@lmc/ui-kit'
import { Form, Row } from 'antd'
import { useCanEditServiceUserData } from 'components/AccessControl'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { ProTag } from 'components/ProTag'
import React, { type FC, useEffect, useRef, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { showToast } from '../../../../../common/utils'
import { Spinner } from '../../../../../components/generic'
import PdfExport from '../../../../../components/pdf/PdfExport'
import { history } from '../../../../../store'
import { withRouter } from '../../../../../withRouter'
import PDFHeader from '../../ResidentConsentScreens/ConsentFormScreen/PDFScreen/PDFHeader'
import { getTitle } from '../utils'

interface AboutMeSectionProps {
  aboutMe: AboutMe
  carers: Carer[]
  saveForm?: (form: any, id: string) => Promise<any>
  fetchAboutMe?: (id: string) => Promise<any>
  router: RouterProps
  requestAboutMe?: boolean
  printPDF?: boolean
  resident: Resident
  containerRef?: any
  onHistoryBack: () => void
  testId?: string
}

export enum SectionType {
  importantAboutMe = 'importantAboutMe',
  importantPeople = 'importantPeople',
  communicationPreferences = 'communicationPreferences',
  myWellness = 'myWellness',
  doAndDont = 'doAndDont',
  howAndWhen = 'howAndWhen',
  worthKnowing = 'worthKnowing'
}

const isDesktop = isBrowser || isTablet

export const AboutMeSection: FC<AboutMeSectionProps> = ({
  saveForm,
  fetchAboutMe,
  aboutMe,
  carers,
  router,
  requestAboutMe = true,
  printPDF,
  resident,
  containerRef,
  onHistoryBack,
  testId
}) => {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const residentId = router.params.id

  const canEditServiceUserData = useCanEditServiceUserData()

  const [editMode, setEditMode] = React.useState(false)

  const [loading, setLoading] = React.useState(true)

  const [formChange, setFormChange] = useState(aboutMe)
  // isFieldTouched doesn't work for unregistered fields (RichEditor has no Form.Item)
  const richTextTouched = useRef<Set<string>>(new Set())
  const overLimitFields = useRef<Set<string>>(new Set())

  const styles = {
    footer: {
      justifyContent: 'flex-end',
      width: isBrowser || isTablet ? 'calc(100vw - 290px)' : '100vw'
    },
    logoIcon: {
      width: 24,
      height: 24
    },
    historyButton: { marginRight: 8 },
    pdfWrapper: {
      position: 'absolute' as any,
      top: 0,
      left: 0,
      width: 1190,
      minHeight: 1480,
      zIndex: 1000,
      background: 'white',
      padding: '24px 28px'
    }
  }

  const scrollToTop = () => {
    if (containerRef) containerRef.current.scrollIntoView()
    window.scrollTo(0, 0)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (requestAboutMe) {
      fetchAboutMe?.(residentId).then(() => {
        setLoading(false)
      })
      handleCloseForm()
    }
    scrollToTop()
  }, [residentId])

  const onFinish = async (_values: AboutMe) => {
    if (overLimitFields.current.size > 0) {
      showToast({
        type: 'warning',
        title: t('general.forms.characterLimitExceeded')
      })
      return
    }
    // Only send fields the user actually touched — avoids leaking server-managed
    // metadata (editedBy, editedOn, _id, etc.) loaded into the form store
    const tempValues: Record<string, any> = {}
    Object.values(SectionType).forEach((type) => {
      if (richTextTouched.current.has(type)) {
        tempValues[type] = formChange[type as keyof AboutMe]
      }
      if (form.isFieldTouched(`${type}WrittenBy`)) {
        tempValues[`${type}WrittenBy`] = form.getFieldValue(`${type}WrittenBy`)
      }
    })
    setLoading(true)
    try {
      const rest = await saveForm?.({ ...tempValues }, residentId)
      if (rest) {
        await fetchAboutMe?.(residentId)
        showToast({ title: t('general.forms.successfullySaved') })
        handleCloseForm()
      }
    } catch (e) {
      showToast({
        type: 'error',
        title: t('general.forms.internalError')
      })
    } finally {
      setLoading(false)
    }
  }

  const handleOpenForm = () => {
    form.setFieldsValue(aboutMe)
    setFormChange(aboutMe)
    richTextTouched.current = new Set()
    overLimitFields.current = new Set()
    setEditMode(true)
  }

  const handleSubmit = async () => {
    await form.submit()
  }

  const onFinishFailed = (error: any) => {
    let title = t('general.forms.missingFieldsWarning')
    if (error && error.errorFields) {
      title = error?.errorFields[0]?.errors[0]
    }
    showToast({ type: 'warning', title })
  }

  const handleCloseForm = () => {
    setEditMode(false)
    setFormChange({} as AboutMe)
    richTextTouched.current = new Set()
    overLimitFields.current = new Set()
    form.resetFields()
  }

  const handleRichTextChange = (source: string, html: string) => {
    // Server ignores null/"" — send raw TipTap HTML (e.g. "<p>&nbsp;</p>" for empty);
    // hasMeaningfulStoredHtml is used for display logic only, not for the stored value
    const value = html
    form.setFields([{ name: source, value }])
    setFormChange((prev) => ({ ...prev, [source]: value }))
    richTextTouched.current.add(source)
    if (textLength(html) > CHAR_LIMIT) {
      overLimitFields.current.add(source)
    } else {
      overLimitFields.current.delete(source)
    }
  }

  const aboutMeSections = [
    {
      source: SectionType.importantAboutMe,
      label: t(`aboutMe.sections.importantAboutMe`),
      tooltip: t(`aboutMe.sections.importantAboutMeTooltip`)
    },
    {
      source: SectionType.importantPeople,
      label: t(`aboutMe.sections.importantPeople`),
      tooltip: t(`aboutMe.sections.importantPeopleTooltip`)
    },
    {
      source: SectionType.communicationPreferences,
      label: t(`aboutMe.sections.communicationPreferences`),
      tooltip: t(`aboutMe.sections.communicationPreferencesTooltip`)
    },
    {
      source: SectionType.myWellness,
      label: t(`aboutMe.sections.myWellness`),
      tooltip: t(`aboutMe.sections.myWellnessTooltip`)
    },
    {
      source: SectionType.doAndDont,
      label: t(`aboutMe.sections.doAndDont`),
      tooltip: t(`aboutMe.sections.doAndDontTooltip`)
    },
    {
      source: SectionType.howAndWhen,
      label: t(`aboutMe.sections.howAndWhen`),
      tooltip: t(`aboutMe.sections.howAndWhenTooltip`)
    },
    {
      source: SectionType.worthKnowing,
      label: t(`aboutMe.sections.worthKnowing`),
      tooltip: t(`aboutMe.sections.worthKnowingTooltip`)
    }
  ]

  const pdfUrl = `residents/${residentId}/pdf/about-me`

  const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

  const checkFormData = aboutMe?.resident
    ? aboutMe?.resident !== residentId
    : false

  if (checkFormData) {
    return (
      <Spinner
        data-testid={testId ? `${testId}-about-me-loading` : 'about-me-loading'}
      />
    )
  }

  const getPDFResidentName = () =>
    `${resident?.name?.first} ${resident?.name?.last} - ${t(`aboutMe.title`)}`

  const getTitleWithResidentName = () =>
    `${t(`aboutMe.titleAbout`)} ${resident?.name?.first} ${
      resident?.name?.last
    }`

  return (
    <div
      style={printPDF ? styles.pdfWrapper : {}}
      data-testid={testId ? `${testId}-about-me-section` : 'about-me-section'}
    >
      <NavigationModal
        canNavigate={
          !(
            editMode &&
            (form.isFieldsTouched() || richTextTouched.current.size > 0)
          )
        }
      />
      <Row>
        {printPDF && (
          <PDFHeader
            resident={resident as any}
            headingExtra={` - ${t(`aboutMe.title`)}`}
          />
        )}
      </Row>
      {!printPDF && (
        <PageHeader
          sticky
          title={getTitleWithResidentName()}
          subtitle={getTitle(
            aboutMe?.editedBy as string,
            // @ts-ignore
            aboutMe?.editedOn,
            t(`aboutMe.reviewed`),
            carers
          )}
          onBack={onHistoryBack ? () => onHistoryBack?.() : undefined}
          data-testid={
            testId ? `${testId}-about-me-page-header` : 'about-me-page-header'
          }
        >
          <ProTag
            data-testid={
              testId ? `${testId}-about-me-pro-tag` : 'about-me-pro-tag'
            }
          />
          {requestAboutMe && (
            <SpaceBetweenWrapper
              data-testid={
                testId
                  ? `${testId}-about-me-actions-wrapper`
                  : 'about-me-actions-wrapper'
              }
            >
              <FlexWrapper>
                <div>
                  <Button
                    style={styles.historyButton}
                    variant="default"
                    onClick={() =>
                      history.push(`/residents/${residentId}/about-me/history`)
                    }
                    data-testid={
                      testId
                        ? `${testId}-about-me-history-button`
                        : 'about-me-history-button'
                    }
                  >
                    {t(`aboutMe.history`)}
                  </Button>
                </div>
                <div>
                  <PdfExport
                    pdfTitle={getPDFResidentName()}
                    url={url}
                    text={isDesktop ? t(`general.buttons.exportPDF`) : 'PDF'}
                    data-testid={
                      testId
                        ? `${testId}-about-me-export-pdf`
                        : 'about-me-export-pdf'
                    }
                  />
                </div>
              </FlexWrapper>
            </SpaceBetweenWrapper>
          )}
        </PageHeader>
      )}
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        scrollToFirstError
        onFinishFailed={onFinishFailed}
        onChange={(e: any) =>
          setFormChange((prev) => ({
            ...prev,
            [e.target.id]: e.target.value
          }))
        }
        data-testid={testId ? `${testId}-about-me-form` : 'about-me-form'}
      >
        {aboutMeSections.map((section, index) => {
          const localisation = {
            description: t(`aboutMe.description`),
            writtenBy: t(`aboutMe.writtenBy`)
          }
          return (
            <MultiTextArea
              formChange={formChange}
              printPDF={printPDF}
              key={section.source}
              heading={section.label}
              headingInfo={getTitle(
                aboutMe?.[`${section.source}EditedBy`] as string,
                // @ts-ignore
                aboutMe?.[`${section.source}EditedOn`],
                t(`aboutMe.reviewed`),
                carers
              )}
              tooltip={section.tooltip}
              onRichTextChange={handleRichTextChange}
              fields={[
                {
                  source: section.source,
                  // @ts-ignore
                  value: aboutMe?.[section.source],
                  element: 'textarea',
                  editMode,
                  label: section.label,
                  localisation
                },
                {
                  source: `${section.source}WrittenBy`,
                  // @ts-ignore
                  value: aboutMe?.[`${section.source}WrittenBy`],
                  element: 'input',
                  editMode,
                  label: section.label,
                  localisation
                }
              ]}
              printPDFPageBreak={false}
              data-testid={
                testId
                  ? `${testId}-about-me-section-${index}`
                  : `about-me-section-${index}`
              }
            />
          )
        })}
      </Form>
      {requestAboutMe && !printPDF && (
        <div
          className="lmc-fixed-footer"
          style={{
            ...styles.footer
          }}
          data-testid={testId ? `${testId}-about-me-footer` : 'about-me-footer'}
        >
          {editMode ? (
            <>
              <Button
                onClick={handleCloseForm}
                variant="default"
                data-testid={
                  testId
                    ? `${testId}-about-me-cancel-button`
                    : 'about-me-cancel-button'
                }
              >
                {t(`general.buttons.cancel`)}
              </Button>
              <Button
                onClick={handleSubmit}
                variant="primary"
                style={{ marginLeft: 8 }}
                isLoading={loading}
                disabledByPermission={!canEditServiceUserData}
                data-testid={
                  testId
                    ? `${testId}-about-me-save-button`
                    : 'about-me-save-button'
                }
              >
                {t(`general.buttons.save`)}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleOpenForm}
              variant="primary"
              disabledByPermission={!canEditServiceUserData}
              data-testid={
                testId
                  ? `${testId}-about-me-edit-button`
                  : 'about-me-edit-button'
              }
            >
              {t(`general.buttons.edit`)}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const SpaceBetweenWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

export default withRouter(AboutMeSection)
